// MinhasComprasPerfil.js — "Minhas Compras" DENTRO da área de perfil (aba meus-pedidos).
// Em vez de rotas separadas, gerencia internamente as sub-telas:
//   list (lista de pedidos) → detalhe (stepper + dados) → cancelar (motivo → confirmação).
// O cancelamento é registrado localmente (stub) e refletido imediatamente na lista,
// para o cliente ver que a solicitação foi feita mesmo sem endpoint no backend.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../services/context/AuthContext";
import useProfileData from "../../hooks/userProfileData";
import useOrderData from "../../hooks/useOrderData";
import Pagination from "../utils/Pagination";
import { TextArea } from "../ui/Formulario";
import {
  centavosParaReais, fmtBRL, fmtPreco, formatDate, formatDateTime, previsaoEntrega, capaUrl, autorDe,
  statusPedidoLabel, statusBadgeColor, pagamentoDoPedido, buildDeliverySteps, pedidoTemEntrega, toDate,
  cancelStateOf, marcarPedidoCancelado,
} from "./comprasUtils";
// A conversão de coin em dinheiro tem UMA fonte na loja (o espelho dela no
// servidor é `config/insigne.php`). O servidor manda quantas coins o pedido
// gastou; quanto isso vale em reais se calcula aqui, com a mesma constante que
// o checkout usou para mostrar o desconto.
import { coinsEmReais } from "../../config/coins";

const main = process.env.REACT_APP_MAIN;
const API_URL = process.env.REACT_APP_API;

const MOTIVOS = [
  { value: "nao_esperava", label: "Não era o que eu esperava" },
  { value: "mais_barato", label: "Encontrei mais barato" },
  { value: "outros", label: "Outros" },
];

// ---------- Sub-componentes visuais ----------
//
// A TRILHA DA ENTREGA. Só aparece em pedido que tem o que entregar — quem
// decide é `pedidoTemEntrega`, lá no detalhe.
//
// O texto vai EMBAIXO da bolinha de propósito: ao lado, cinco etapas não cabem
// em 400px sem virar abreviação.
const Stepper = ({ steps }) => (
  <div className="flex items-start">
    {steps.map((s, i) => {
      const ativo = s.done || s.current;
      const proximoAtivo = i < steps.length - 1 && (steps[i + 1].done || steps[i + 1].current);
      return (
        <React.Fragment key={s.key}>
          <div className="flex flex-col items-center text-center w-[20%] min-w-0">
            <div className={"w-10 h-10 rounded-full flex items-center justify-center shrink-0 " + (ativo ? "bg-insigne-orange text-paper" : "border-2 border-line text-white/40")}><i className={"bi " + s.icon} /></div>
            <p className={"mt-2 text-[11px] md:text-xs " + (s.current ? "font-bold text-white" : ativo ? "text-white/90" : "text-white/45")}>{s.label}</p>
            {s.data && s.data !== "-" && <p className="text-[10px] text-white/40 mt-0.5">{s.data}</p>}
          </div>
          {/* O trecho ainda não percorrido era `bg-white/15`. No tema claro o
              branco a 15% sobre bege some, e a trilha aparecia partida: bolinhas
              soltas, sem fio ligando uma etapa à outra. `bg-line` é a mesma
              divisória do resto do site e existe nos dois temas. */}
          {i < steps.length - 1 && <div className={"flex-1 h-0.5 mt-5 " + (proximoAtivo ? "bg-insigne-orange" : "bg-line")} />}
        </React.Fragment>
      );
    })}
  </div>
);

const Bloco = ({ titulo, children }) => (
  <section className="bloco p-5">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-white/45 mb-3">{titulo}</h3>
    {children}
  </section>
);

const StatusBadge = ({ order }) => (
  <span className={"px-2 py-0.5 rounded-md text-xs font-semibold " + statusBadgeColor(order)}>{statusPedidoLabel(order)}</span>
);

// ===== AS LINHAS DO REGISTRO =====
//
// Duas origens, uma lista só (pedido de 11/09/2026: "registre todas as compras,
// mesmo se for usando ticket ou coin"):
//
//   PEDIDO ....... o que passou pelo pagamento — livros, e também os pacotes de
//                  Tickets e de Coins, que são pedidos de tipo próprio.
//   CRÉDITO ...... o que NÃO gera pedido porque não passa por gateway: Ticket
//                  comprado com Coins, e Ticket gasto no Clube. Vem do extrato
//                  de tickets (/creditos/extrato).
//
// Sem a segunda origem, quem comprasse tudo com o saldo veria um registro vazio
// e concluiria que a compra não aconteceu.

/** Quantos Tickets/Coins o pedido entrega, quando ele é de crédito. */
const creditoDoPedido = (o) => {
  const tipo = String(o?.tipo || "");
  if (tipo !== "tickets" && tipo !== "coins") return null;
  const quantidade = Number(o?.creditos_quantidade) || 0;
  return {
    quantidade,
    ticket: tipo === "tickets",
    rotulo: tipo === "tickets"
      ? `${quantidade} ${quantidade === 1 ? "Ticket" : "Tickets"}`
      : `${quantidade} Coins`,
  };
};

/**
 * O retângulo colorido que substitui a capa nas compras de crédito.
 *
 * A MEDIDA É A DA CAPA (40x56), e não um quadrado: numa lista que mistura
 * livro e crédito, dois tamanhos diferentes à esquerda desalinham o texto de
 * uma linha para a outra — foi o que o dono viu no card de Ticket.
 */
const IconeCredito = ({ ticket, tamanho = "h-14 w-10" }) => (
  <span
    className={
      "inline-flex shrink-0 items-center justify-center rounded-lg border text-lg " + tamanho + " " +
      (ticket
        ? "border-insigne-ticket/35 bg-insigne-ticket/15 text-insigne-ticket"
        : "border-insigne-orange/35 bg-insigne-orange/15 text-insigne-orange")
    }
  >
    <i className={"bi " + (ticket ? "bi-ticket-perforated-fill" : "bi-coin")} />
  </span>
);

/** A aba de E-books do perfil. Um lugar só: o "Baixe aqui" abre a mesma. */
const irParaMeusLivros = () =>
  window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: "meus-livros" }));

/** A faixa de estado: um ícone, o que aconteceu, e um caminho quando há. */
const LinhaDeEstado = ({ icone, cor, titulo, descricao, acao }) => (
  <div className="bloco p-5 flex flex-wrap items-center gap-3.5">
    <i className={"bi " + icone + " text-2xl shrink-0 " + cor} />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-white">{titulo}</p>
      {descricao && <p className="text-xs text-white/55 mt-0.5">{descricao}</p>}
    </div>
    {acao}
  </div>
);

/**
 * O QUE APARECE NO LUGAR DA TRILHA QUANDO NÃO HÁ O QUE ENTREGAR.
 *
 * O dono, em 11/09/2026: "quando clico em um pedido que foi compra de Ebook ele
 * dá o progresso do envio como se fosse livro físico". Dava mesmo — e pacote de
 * Ticket e de Coins também: as cinco etapas eram desenhadas para todo pedido,
 * prometendo "Em Produção" e "Saída para Entrega" a uma compra que nunca sai de
 * lugar nenhum.
 *
 * O que esta faixa diz sai do ESTADO REAL do pedido (`status`, e o cancelamento
 * consolidado que o detalhe já resolveu antes de chegar aqui), nunca do tempo
 * passado desde a compra: e-book só está na estante depois do pagamento
 * confirmado, e afirmar o contrário é o mesmo erro do caminhão, em menor escala.
 */
const EstadoSemEntrega = ({ order }) => {
  const credito = creditoDoPedido(order);
  const pago = order?.status === "Pagamento confirmado";
  const itens = Array.isArray(order?.livros) ? order.livros : [];

  // Pendente é pendente para todos: sem pagamento confirmado não há arquivo na
  // estante nem saldo na carteira, e prometer qualquer uma das duas coisas aqui
  // é o que faz a pessoa procurar o que ainda não existe.
  if (!pago) {
    return (
      <LinhaDeEstado
        icone="bi-hourglass-split"
        cor="text-amber-300"
        titulo="Aguardando confirmação do pagamento"
        descricao={credito
          ? `${credito.rotulo} entram na sua conta assim que o pagamento for confirmado.`
          : "Assim que o pagamento for confirmado, a compra fica disponível na sua conta."}
      />
    );
  }

  if (credito) {
    return (
      <LinhaDeEstado
        icone={credito.ticket ? "bi-ticket-perforated-fill" : "bi-coin"}
        cor={credito.ticket ? "text-insigne-ticket" : "text-insigne-orange"}
        titulo="Creditado na sua conta"
        descricao={`${credito.rotulo} · já disponíveis para usar.`}
      />
    );
  }

  if (itens.length > 0) {
    return (
      <LinhaDeEstado
        icone="bi-unlock-fill"
        cor="text-insigne-orange"
        titulo="Liberado na sua conta"
        descricao="Compra digital: não há envio. Os arquivos ficam na aba E-books."
        acao={
          <button
            onClick={irParaMeusLivros}
            className="btn-solido rounded-controle inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold foco-laranja"
          >
            <i className="bi bi-book-half" /> Ver em E-books
          </button>
        }
      />
    );
  }

  // Pedido sem item e sem crédito (assinatura do Clube, por exemplo): diz o que
  // se sabe — o pagamento passou — e não inventa uma entrega nem uma estante.
  return (
    <LinhaDeEstado
      icone="bi-check-circle-fill"
      cor="text-emerald-300"
      titulo="Pagamento confirmado"
      descricao="Este pedido não tem envio."
    />
  );
};

const LinhaDePedido = ({ order, onDetalhe }) => {
  const livros = Array.isArray(order.livros) ? order.livros : [];
  const credito = creditoDoPedido(order);
  const estado = cancelStateOf(order);
  const pagamento = pagamentoDoPedido(order);
  const capas = livros.filter((l) => capaUrl(l)).slice(0, 3);
  const titulo = credito
    ? credito.rotulo
    : livros.length === 0
      ? `Pedido ${order.id}`
      : livros.length === 1
        ? (livros[0].titulo || `Livro #${livros[0].id}`)
        : `${livros[0].titulo || "Livro"} e mais ${livros.length - 1}`;

  return (
    <article
      className="flex items-center gap-3.5 cartao px-3.5 py-3
                 transition-colors hover:border-white/20"
    >
      {/* As capas empilhadas: três bastam para dizer "foram vários" — a lista
          inteira vive no detalhe. */}
      {credito ? (
        <IconeCredito ticket={credito.ticket} />
      ) : capas.length > 0 ? (
        <div className="flex shrink-0 -space-x-3">
          {capas.map((l, i) => (
            <img
              key={`${l.id}-${i}`}
              src={capaUrl(l)}
              alt=""
              loading="lazy"
              className="h-14 w-10 rounded-md border border-white/10 object-cover shadow-md"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ))}
        </div>
      ) : (
        <span className="inline-flex h-14 w-10 shrink-0 items-center justify-center rounded-md
                         border border-line bg-insigne-navy-deep text-white/30">
          <i className="bi bi-book" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white" title={titulo}>{titulo}</p>
        <p className="mt-0.5 truncate text-xs text-white/45">
          Nº {order.id} · {formatDateTime(order.created_at)} · {pagamento.rotulo}
          {!credito && livros.length > 1 && ` · ${livros.length} itens`}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <StatusBadge order={order} />
          {estado === "cancelado" && (
            <span className="inline-flex items-center gap-1 text-[11px] text-red-300/80">
              <i className="bi bi-x-circle" /> Cancelado
            </span>
          )}
          {estado === "solicitado" && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-300/80">
              <i className="bi bi-clock-history" /> Cancelamento em análise
            </span>
          )}
        </div>
      </div>

      <div className="shrink-0 text-right">
        <p className="text-sm font-bold text-white">{fmtBRL(centavosParaReais(order.valorTotal))}</p>
        {pagamento.coins > 0 && (
          // Coins usadas na compra: elas são parte do que a pessoa "pagou", e
          // sem esta linha o registro esconde metade do preço.
          //
          // O número vem de `coins_gastas` (o extrato, do servidor) e NÃO da
          // coluna `insigneCoins`, que guarda o que o navegador ENVIOU: quem
          // manda 500 coins num pedido de R$ 30 tem 300 debitadas, e a linha
          // cobrava as 500 que nunca saíram do saldo.
          <p className="text-[11px] text-insigne-orange">
            {pagamento.soCoins ? `${pagamento.coins} Coins` : `−${pagamento.coins} Coins`}
          </p>
        )}
        {/* O detalhe é dos pedidos de livro: é lá que moram as etapas da
            entrega e o endereço. Pacote de crédito não tem entrega. */}
        {!credito && (
          <button
            onClick={onDetalhe}
            className="mt-1 text-xs font-medium text-insigne-orange hover:underline"
          >
            Ver detalhes
          </button>
        )}
      </div>
    </article>
  );
};

/** Quantas Coins pagaram um Ticket — o número está na descrição do movimento. */
const coinsDaDescricao = (texto) => {
  const m = String(texto || "").match(/(\d+)\s*coins/i);
  return m ? Number(m[1]) : 0;
};

const LinhaDeCredito = ({ mov }) => {
  const compraComCoins = mov.motivo === "compra_coins";
  const quantidade = Number(mov.quantidade) || 0;
  const tickets = `${quantidade} ${quantidade === 1 ? "Ticket" : "Tickets"}`;
  const coins = coinsDaDescricao(mov.descricao);

  return (
    <article className="flex items-center gap-3.5 cartao px-3.5 py-3">
      <IconeCredito ticket />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">
          {compraComCoins ? tickets : `Clube Insigne · ${tickets}`}
        </p>
        <p className="mt-0.5 truncate text-xs text-white/45">
          {formatDateTime(mov.created_at)} ·{" "}
          {compraComCoins ? "Trocado por Coins na Loja de Créditos" : "Ticket usado para entrar ou renovar"}
        </p>
        <div className="mt-1.5">
          <span className="inline-flex items-center gap-1 rounded-selo border border-emerald-400/30
                           bg-emerald-400/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
            <i className="bi bi-check-circle-fill" /> Concluído
          </span>
        </div>
      </div>

      <div className="shrink-0 text-right">
        {compraComCoins ? (
          <p className="text-sm font-bold text-insigne-orange">
            {coins > 0 ? `${coins} Coins` : "Pago com Coins"}
          </p>
        ) : (
          <p className="text-sm font-bold text-insigne-ticket">{tickets}</p>
        )}
        <p className="text-[11px] text-white/40">sem dinheiro</p>
      </div>
    </article>
  );
};

const MinhasComprasPerfil = () => {
  const { user } = useAuth();
  const { profileData } = useProfileData();
  const { orders, loading, error, refetch } = useOrderData(user);

  const [view, setView] = useState("list"); // list | detalhe | cancelar
  const [selId, setSelId] = useState(null);
  const [ano, setAno] = useState("todos");
  const [tick, setTick] = useState(0); // força recálculo após marcar cancelamento

  // etapas do cancelamento
  const [etapa, setEtapa] = useState(1);
  const [motivo, setMotivo] = useState("outros");
  const [detalhe, setDetalhe] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erroCancel, setErroCancel] = useState("");

  const order = useMemo(() => orders.find((o) => String(o.id) === String(selId)), [orders, selId]);

  // ===== A SEGUNDA ORIGEM DO REGISTRO =====
  //
  // O extrato de Tickets traz o que NÃO vira pedido: Ticket comprado com Coins
  // e Ticket gasto no Clube. Falha em silêncio de propósito — sem ele o
  // registro ainda mostra os pedidos, e um erro aqui não pode esconder as
  // compras que existem.
  const [movimentos, setMovimentos] = useState([]);
  useEffect(() => {
    if (!user) return undefined;
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}creditos/extrato`, {
          headers: { Accept: "application/json", Authorization: "Bearer " + localStorage.getItem("token") },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (vivo) setMovimentos(Array.isArray(d?.movimentos) ? d.movimentos : []);
      } catch { /* o registro funciona só com os pedidos */ }
    })();
    return () => { vivo = false; };
  }, [user]);

  // As duas origens numa lista só, da mais recente para a mais antiga.
  const registros = useMemo(() => {
    const dePedidos = orders.map((o) => ({
      chave: `p${o.id}`, tipo: "pedido", data: toDate(o.created_at), pedido: o,
    }));
    const deCreditos = movimentos
      // Só o que é COMPRA e não tem pedido: `compra` (pacote pago em reais) já
      // está na lista como pedido, e ajuste de ADM não é compra de ninguém.
      .filter((m) => m.motivo === "compra_coins" || m.motivo === "clube")
      .map((m) => ({ chave: `m${m.id}`, tipo: "credito", data: toDate(m.created_at), mov: m }));

    return [...dePedidos, ...deCreditos].sort(
      (a, b) => (b.data ? b.data.getTime() : 0) - (a.data ? a.data.getTime() : 0)
    );
  }, [orders, movimentos]);

  const anos = useMemo(() => {
    const set = new Set();
    registros.forEach((r) => { if (r.data) set.add(r.data.getFullYear()); });
    return Array.from(set).sort((a, b) => b - a);
  }, [registros]);

  const registrosDoAno = useMemo(() => {
    if (ano === "todos") return registros;
    return registros.filter((r) => r.data && r.data.getFullYear() === Number(ano));
  }, [registros, ano]);

  // ===== PAGINAÇÃO (pedido do dono, 11/09/2026) =====
  //
  // Oito por página: quatro fileiras de dois. O histórico não tem teto — quem
  // compra Ticket todo mês chega a dezenas de linhas — e uma página que cresce
  // para sempre é onde a pessoa desiste de procurar.
  const POR_PAGINA_COMPRAS = 8;
  const [paginaCompras, setPaginaCompras] = useState(1);
  const totalPaginasCompras = Math.max(1, Math.ceil(registrosDoAno.length / POR_PAGINA_COMPRAS));
  const paginaCompra = Math.min(paginaCompras, totalPaginasCompras);
  const inicioCompras = (paginaCompra - 1) * POR_PAGINA_COMPRAS;
  const registrosDaPagina = registrosDoAno.slice(inicioCompras, inicioCompras + POR_PAGINA_COMPRAS);

  // Trocar o ano invalida a página: filtrar da página 3 com uma página de
  // resultado mostraria uma lista vazia.
  useEffect(() => { setPaginaCompras(1); }, [ano]);

  const abrirDetalhe = (id) => { setSelId(id); setView("detalhe"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const abrirCancelar = (id) => { setSelId(id); setView("cancelar"); setEtapa(1); setMotivo("outros"); setDetalhe(""); setErroCancel(""); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const voltarLista = () => { setView("list"); setSelId(null); };

  const finalizarCancelamento = async () => {
    setEnviando(true);
    setErroCancel("");
    const motivoTxt = motivo === "outros" ? detalhe.trim() : MOTIVOS.find((m) => m.value === motivo)?.label;
    try {
      const token = localStorage.getItem("token");
      let resp;
      try {
        resp = await fetch(`${API_URL}pedido-editora/${selId}/cancelar`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
          body: JSON.stringify({ motivo: motivoTxt, usuario_editora_id: user }),
        });
      } catch {
        // Falha real de rede: não confirma nada (deixa o cliente tentar de novo).
        setErroCancel("Não foi possível enviar a solicitação. Verifique sua conexão e tente novamente.");
        return;
      }
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setErroCancel(data.error || "Não foi possível solicitar o cancelamento deste pedido.");
        return;
      }
      // Sucesso confirmado pelo servidor: marca local (feedback imediato) e recarrega
      // a lista para refletir o cancelamento_status real do backend.
      marcarPedidoCancelado(selId, motivoTxt);
      if (refetch) { try { await refetch(); } catch { /* mantém a marca local */ } }
      setTick((t) => t + 1);
      setEtapa(2);
    } finally {
      setEnviando(false);
    }
  };

  if (!user) return <div className="text-white/60 py-10 text-center">Você precisa estar logado para ver suas compras.</div>;

  // ===== LISTA =====
  if (view === "list") {
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-1 flex-wrap">
          <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] flex items-center gap-2">
            <i className="bi bi-bag-check" /> Registro de Compras
          </h2>
          {anos.length > 0 && (
            <select
              value={ano}
              onChange={(e) => setAno(e.target.value)}
              className="rounded-lg bg-insigne-navy-soft border border-line text-sm px-4 py-2 outline-none"
            >
              <option value="todos">Todos os anos</option>
              {anos.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
        </div>
        <p className="text-white/50 text-sm mb-5">
          Tudo o que você comprou: livros, Tickets e Coins, inclusive o que foi pago com eles.
        </p>

        {loading ? (
          <div className="text-center text-white/50 py-16">Carregando suas compras...</div>
        ) : error ? (
          <div className="text-center text-red-300 py-16">Ocorreu um erro ao carregar suas compras.</div>
        ) : registrosDoAno.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <i className="bi bi-bag-x text-5xl text-white/30 mb-4" />
            <h3 className="text-lg font-bold">Nenhuma compra {ano === "todos" ? "por aqui" : `em ${ano}`}</h3>
            <Link
              to={main + "livros"}
              className="mt-4 rounded-lg px-5 py-2.5 text-sm font-semibold bg-insigne-orange text-paper hover:bg-insigne-orange-dark"
            >
              Ver livros
            </Link>
          </div>
        ) : (
          // ===== A LISTA, EM LINHAS =====
          //
          // Cada compra era um cartão de três faixas (cabeçalho largo, lista de
          // livros com "Previsão de Entrega" à direita, e rodapé de ações) que
          // ocupava a largura toda para dizer pouco — o dono pediu para
          // organizar melhor. Agora é UMA LINHA por compra: as capas
          // empilhadas à esquerda, o que foi comprado no meio, e o valor com o
          // caminho para o detalhe à direita. O que saiu daqui (previsão de
          // entrega, endereço, etapas) não sumiu: mora no detalhe, que é onde
          // se vai quando a pergunta deixa de ser "o que eu comprei?".
          <>
            <p className="text-xs text-white/45 mb-3">
              Mostrando {inicioCompras + 1}–
              {Math.min(inicioCompras + POR_PAGINA_COMPRAS, registrosDoAno.length)} de{" "}
              {registrosDoAno.length}
            </p>

            <div id="compras-topo" className="h-0 scroll-mt-24 md:scroll-mt-[280px]" aria-hidden="true" />

            {/* DUAS POR FILEIRA a partir de 1024px. Uma linha por compra
                atravessava a coluna inteira para dizer "12 Tickets · R$ 400" —
                metade da largura ficava vazia. Duas colunas cortam a altura da
                lista pela metade sem apertar nada: o que não cabe (forma de
                pagamento, itens) já vive no detalhe. */}
            <div className="grid gap-2.5 lg:grid-cols-2">
              {registrosDaPagina.map((r) =>
                r.tipo === "pedido" ? (
                  <LinhaDePedido
                    key={`${r.chave}-${tick}`}
                    order={r.pedido}
                    onDetalhe={() => abrirDetalhe(r.pedido.id)}
                  />
                ) : (
                  <LinhaDeCredito key={r.chave} mov={r.mov} />
                )
              )}
            </div>

            <Pagination
              pagina={paginaCompra}
              totalPaginas={totalPaginasCompras}
              onChange={setPaginaCompras}
              rolarAte="compras-topo"
            />
          </>
        )}
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <button onClick={voltarLista} className="text-insigne-orange text-sm hover:underline mb-4"><i className="bi bi-arrow-left" /> Voltar</button>
        <div className="text-center py-12 text-white/60">Pedido não encontrado.</div>
      </div>
    );
  }

  const livros = Array.isArray(order.livros) ? order.livros : [];
  const estado = cancelStateOf(order);

  // ===== DETALHE =====
  if (view === "detalhe") {
    // A TRILHA SÓ EXISTE PARA QUEM TEM O QUE ENTREGAR.
    //
    // `buildDeliverySteps` continua igual e continua servindo a quem perguntar;
    // o que mudou é que a tela só pergunta quando a pergunta faz sentido. Sem
    // este `if`, um pedido de e-book — ou um pacote de Coins — desenhava as
    // cinco etapas de logística, que foi o defeito relatado pelo dono.
    const temEntrega = pedidoTemEntrega(order);
    const steps = temEntrega ? buildDeliverySteps(order) : [];
    const pagamento = pagamentoDoPedido(order);
    const endereco = (() => {
      const o = order, p = profileData || {};
      const end = o.endereco || p.endereco;
      if (!end) return "Endereço não informado.";
      return `${end}${o.numero || p.numero ? ", Nº " + (o.numero || p.numero) : ""}. ${o.cidade || p.cidade || ""}${o.estado || p.estado ? "/" + (o.estado || p.estado) : ""}${o.cep || p.cep ? " - " + (o.cep || p.cep) : ""}`;
    })();
    return (
      <div>
        <button onClick={voltarLista} className="text-insigne-orange text-sm hover:underline mb-4"><i className="bi bi-arrow-left" /> Voltar para Minhas Compras</button>

        {/* O CABEÇALHO DO PEDIDO.
            Era uma fileira de textos soltos ("Pedido 12  Comprado: ...  Total:
            ...") fechada por uma régua de 1px: três informações com o mesmo
            peso, nenhuma hierarquia, e o estado da compra só aparecendo lá
            embaixo, dentro da trilha. Agora é um painel de verdade: o número
            identifica, o selo diz onde a compra está, data e total qualificam,
            e o cancelar fica na ponta — fora do caminho de leitura, que é onde
            uma ação destrutiva deve ficar. */}
        <header className="bloco p-5 mb-6">
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
            <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem]">Pedido {order.id}</h2>
            <StatusBadge order={order} />
            {(estado === null || estado === "recusado") && order.status_entrega !== "entregue" && (
              <button
                onClick={() => abrirCancelar(order.id)}
                className="ml-auto rounded-controle border-2 border-line px-3.5 py-1.5 text-xs font-semibold
                           text-white/60 transition-colors hover:border-red-400/40 hover:text-red-300 foco-laranja"
              >
                Cancelar Pedido
              </button>
            )}
          </div>
          <div className="mt-3.5 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-white/55">
            <span>Comprado em <span className="font-semibold text-white/85">{formatDate(order.created_at)}</span></span>
            <span>Total <span className="font-semibold text-white">{fmtBRL(centavosParaReais(order.valorTotal))}</span></span>
          </div>
        </header>

        {/* Os avisos de estado usavam `rounded-2xl` com borda de 1px — a
            hairline que o resto do site abandonou. Agora seguem o mesmo
            tratamento das outras superfícies: raio de painel e borda de 2px, na
            cor do estado (a mesma do selo, para o aviso e o selo não parecerem
            dois estados diferentes). */}
        {estado === "cancelado" ? (
          <div className="rounded-painel border-2 border-red-400/30 bg-red-500/10 p-5 mb-6 flex items-center gap-3">
            <i className="bi bi-x-circle-fill text-red-300 text-xl" />
            <p className="text-red-200 text-sm">Este pedido foi cancelado.</p>
          </div>
        ) : estado === "solicitado" ? (
          <div className="rounded-painel border-2 border-amber-400/30 bg-amber-400/10 p-5 mb-6 flex items-center gap-3">
            <i className="bi bi-clock-history text-amber-300 text-xl" />
            <p className="text-amber-200 text-sm">Cancelamento em análise. Você receberá um contato por e-mail assim que a equipe avaliar sua solicitação.</p>
          </div>
        ) : (
          <>
            {estado === "recusado" && (
              <div className="bloco p-4 mb-5 flex items-center gap-3">
                <i className="bi bi-info-circle text-white/50" />
                <p className="text-white/60 text-sm">Sua última solicitação de cancelamento foi recusada pela equipe.</p>
              </div>
            )}
            {temEntrega ? (
              // A trilha precisa de 480px para as cinco etapas não se
              // atropelarem; em 400px ela rola dentro do próprio quadro, e não
              // arrasta a página junto.
              <div className="mb-7 overflow-x-auto"><div className="min-w-[480px]"><Stepper steps={steps} /></div></div>
            ) : (
              <div className="mb-7"><EstadoSemEntrega order={order} /></div>
            )}
          </>
        )}

        {/* O ENDEREÇO SÓ APARECE QUANDO EXISTE ENTREGA.
            Um "Endereço de Entrega" num pedido de e-book é a mesma promessa
            falsa que o caminhão fazia, só que escrita: não há pacote, e o que
            está ali é o endereço de cadastro da pessoa, lido ao vivo do perfil.
            Sem o par, o pagamento ocupa a largura toda em vez de deixar meia
            coluna vazia. */}
        <div className={"grid grid-cols-1 gap-4 mb-4 " + (temEntrega ? "md:grid-cols-2" : "")}>
          <Bloco titulo="Método de Pagamento">
            {/* A FORMA DE PAGAMENTO, COM AS COINS DENTRO (pedido do dono,
                11/09/2026: "ele tem que falar a forma de pagamento incluindo se
                for por coin"). São três casos e os três aparecem:
                  só coins .... o saldo pagou tudo, nenhum gateway foi acionado;
                  em parte .... a forma do gateway E o que as coins abateram;
                  só gateway .. como sempre foi.
                Quem separa os casos é `pagamentoDoPedido`. O laranja é a cor da
                Insigne Coin — o vermelho é do Ticket, e trocar os dois faria a
                tela falar de outra moeda. */}
            <div className="flex items-start gap-3">
              <i className={"bi " + pagamento.icone + " text-xl shrink-0 " + (pagamento.soCoins ? "text-insigne-orange" : "text-white/80")} />
              <div className="min-w-0">
                <p className="text-sm">
                  {pagamento.rotulo}
                  {!pagamento.soCoins && order.installments > 1 ? ` (em ${order.installments}x)` : ""}
                </p>
                {pagamento.soCoins ? (
                  pagamento.coins > 0 && (
                    <p className="mt-1 text-xs text-insigne-orange">
                      {pagamento.coins} {pagamento.coins === 1 ? "Coin" : "Coins"} · {fmtBRL(coinsEmReais(pagamento.coins))}
                    </p>
                  )
                ) : pagamento.coins > 0 && (
                  <p className="mt-1 text-xs text-insigne-orange">
                    <i className="bi bi-coin" /> mais {pagamento.coins} Insigne {pagamento.coins === 1 ? "Coin" : "Coins"}
                    {" "}({fmtBRL(coinsEmReais(pagamento.coins))} de desconto)
                  </p>
                )}
              </div>
            </div>
          </Bloco>
          {temEntrega && (
            <Bloco titulo="Endereço de Entrega"><p className="text-sm text-white/90 leading-relaxed">{endereco}</p></Bloco>
          )}
        </div>

        <Bloco titulo="Pedido">
          <div className="divide-y divide-line">
            {livros.map((item, i) => (
              <div key={`${item.id}-${i}`} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                <div className="w-12 h-16 rounded-card overflow-hidden bg-insigne-navy-deep shrink-0 flex items-center justify-center">
                  {capaUrl(item) ? (
                    <img
                      src={capaUrl(item)}
                      alt={item.titulo}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <i className="bi bi-book text-white/30" />
                  )}
                </div>

                <div className="flex-1 min-w-0 flex justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-semibold text-sm leading-tight">
                      {item.titulo || `Livro #${item.id}`}
                      {item.quantidade > 1 ? ` (x${item.quantidade})` : ""}
                    </h4>
                    {autorDe(item) && <p className="text-white/55 text-xs mt-0.5">{autorDe(item)}</p>}
                  </div>

                  <div className="text-right shrink-0">
                    {item.preco != null && (
                      <p className="text-sm">
                        <span className="text-[10px] align-top">R$</span>{" "}
                        <span className="font-bold">{fmtPreco(item.preco)}</span>
                      </p>
                    )}

                    {/* A previsão é DO ITEM, e não do pedido: um carrinho com um
                        impresso e um e-book tem uma linha que viaja e outra que
                        já está na conta. */}
                    <p className="text-white/45 text-xs mt-1">
                      {item.formato === "fisico"
                        ? "Entrega: " + previsaoEntrega(order.created_at, order.frete_dias)
                        : "E-book"}
                    </p>

                    {/* O histórico é onde a pessoa volta quando esquece o que comprou; sem esta
                        linha o presente só existiria na tela de agradecimento, que ela viu uma vez. */}
                    {item.formato === "fisico" && item.ebook_de_brinde && estado !== "cancelado" && (
                      <p className="text-amber-200 text-xs mt-1">
                        <i className="bi bi-gift-fill" /> E-book incluso para ler no site
                      </p>
                    )}

                    {item.formato !== "fisico" && estado !== "cancelado" && (
                      <button
                        onClick={irParaMeusLivros}
                        className="mt-2 inline-flex items-center gap-1.5 text-insigne-orange text-xs font-semibold hover:underline"
                      >
                        <i className="bi bi-download" /> Baixe aqui
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Bloco>
      </div>
    );
  }

  // ===== CANCELAR =====
  const Steps = () => {
    const Item = ({ n, label, done, current }) => (
      <div className="flex items-center gap-2.5">
        <span className={"w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 " + (done || current ? "bg-insigne-orange text-paper" : "border border-line text-white/50")}>{done ? <i className="bi bi-check-lg" /> : n}</span>
        <span className={"text-sm " + (done || current ? "text-white" : "text-white/50")}>{label}</span>
      </div>
    );
    return (
      <div className="flex items-center gap-4 mb-6">
        <Item n={1} label="Motivo do Cancelamento" done={etapa > 1} current={etapa === 1} />
        {/* `bg-white/20` sumia no tema claro, como o traço da trilha de entrega:
            os dois passos ficavam soltos, sem nada ligando um ao outro. */}
        <div className="w-8 h-px bg-line" />
        <Item n={2} label="Confirmação" current={etapa === 2} />
      </div>
    );
  };

  return (
    <div>
      <button onClick={() => (etapa === 2 ? voltarLista() : setView("detalhe"))} className="text-insigne-orange text-sm hover:underline mb-4"><i className="bi bi-arrow-left" /> Voltar</button>
      <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] mb-6">Cancelar Pedido {order.id}</h2>
      <Steps />

      {etapa === 1 ? (
        <section className="bloco p-6">
          <h3 className="text-lg font-bold">Qual o motivo do cancelamento?</h3>
          <p className="text-white/50 text-sm mt-1 mb-5">Selecione o que te motivou a solicitar o cancelamento:</p>
          <div className="space-y-3">
            {MOTIVOS.map((m) => (
              // O <input> vem ANTES da bolinha desenhada para o `peer` alcançar
              // ela: o rádio de verdade é `sr-only` e o contorno do navegador
              // fica invisível junto com ele — sem isto, quem navega por teclado
              // não vê em qual motivo está.
              <label key={m.value} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="motivo"
                  checked={motivo === m.value}
                  onChange={() => setMotivo(m.value)}
                  className="peer sr-only"
                />
                <span
                  className={
                    "w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center " +
                    "peer-focus-visible:ring-2 peer-focus-visible:ring-insigne-orange " +
                    (motivo === m.value ? "border-insigne-orange" : "border-line")
                  }
                >
                  {motivo === m.value && <span className="w-2 h-2 rounded-full bg-insigne-orange" />}
                </span>
                <span className="text-sm">{m.label}</span>
              </label>
            ))}
          </div>
          {motivo === "outros" && (
            <div className="mt-5">
              <label className="block text-sm font-medium mb-2">Insira o motivo</label>
              {/* O campo é o pronto (`TextArea`/`campoCls`) e não uma receita
                  escrita à mão: era a mesma recitação de classes, com um
                  `focus:` que o Bootstrap anula — o anel de foco do componente
                  é quem comunica onde se está digitando. */}
              <TextArea
                value={detalhe}
                onChange={(e) => setDetalhe(e.target.value)}
                rows={4}
                placeholder="Digite aqui o motivo que te levou ao cancelamento..."
              />
            </div>
          )}
          {erroCancel && <p className="text-red-300 text-sm mt-4 text-center">{erroCancel}</p>}
          <div className="mt-5 flex justify-center">
            <button
              onClick={finalizarCancelamento}
              disabled={enviando || (motivo === "outros" && !detalhe.trim())}
              className="btn-solido rounded-controle px-10 py-2.5 text-sm font-semibold foco-laranja
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {enviando ? "Enviando..." : "Finalizar"}
            </button>
          </div>
        </section>
      ) : (
        <section className="bloco p-6">
          <div className="flex items-center gap-3 mb-3"><i className="bi bi-check-circle-fill text-emerald-400 text-2xl" /><h3 className="text-lg font-bold">Solicitação de cancelamento enviada</h3></div>
          <p className="text-white/70 text-sm leading-relaxed max-w-[640px]">Sua solicitação será <strong>analisada pela nossa equipe</strong>. Assim que for aprovada ou recusada, enviaremos um retorno para o e-mail <span className="text-insigne-orange">{profileData?.email || "seu e-mail cadastrado"}</span>, com instruções sobre o estorno dos valores. Enquanto isso, o pedido aparece como <strong>“Cancelamento em análise”</strong> na sua lista.</p>
          <button
            onClick={voltarLista}
            className="btn-solido rounded-controle mt-6 inline-flex items-center gap-2 px-6 py-2.5
                       text-sm font-semibold foco-laranja"
          >
            <i className="bi bi-arrow-left" /> Voltar ao Minhas Compras
          </button>
        </section>
      )}
    </div>
  );
};

export default MinhasComprasPerfil;
